import React, { useState, useEffect, useContext } from 'react';

import { collection, query, where, doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { AuthContext } from '../App';

// Icons
const HeartIcon = ({ filled = false, className = "" }) => (
  <svg className={className} fill={filled ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 000-6.364 4.5 4.5 0 00-6.364 0L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
  </svg>
);

const CloseIcon = ({ className = "" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const UserIcon = ({ className = "" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const LocationIcon = ({ className = "" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const ChurchIcon = ({ className = "" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2L6 8v14h12V8l-6-6z M12 2v6 M9 22v-6h6v6" />
  </svg>
);

// MessageBox component
function MessageBox({ message, onClose }) {
  if (!message) return null;
  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
      <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-sm w-full text-center border-2" style={{ borderColor: 'hsl(221, 45%, 18%)' }}>
        <p className="text-lg font-semibold mb-6" style={{ color: 'hsl(221, 45%, 18%)' }}>{message}</p>
        <button
          onClick={onClose}
          className="px-6 py-3 rounded-lg shadow-md text-white font-semibold transition-all duration-300 hover:shadow-lg"
          style={{ background: 'linear-gradient(135deg, hsl(221, 45%, 18%) 0%, hsl(221, 35%, 25%) 100%)' }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          OK
        </button>
      </div>
    </div>
  );
}

// Basic Info Modal Component
function BasicInfoModal({ isOpen, onClose, interestData }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
      <div className="rounded-2xl p-6 w-full max-w-sm shadow-2xl" style={{ background: 'linear-gradient(135deg, hsl(221, 45%, 18%) 0%, hsl(221, 35%, 25%) 100%)' }}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-white">Basic Info</h3>
          <button onClick={onClose} className="text-white hover:opacity-80 transition-colors">
            <CloseIcon className="w-5 h-5" />
          </button>
        </div>
        
        <div className="text-center mb-6">
          <img
            src={interestData.senderProfile?.profilePhotoUrl || "https://placehold.co/80x80/E0E7FF/4F46E5?text=U"}
            alt={interestData.senderProfile?.firstName || "User"}
            className="w-20 h-20 rounded-full object-cover mx-auto mb-3 border-4 shadow-lg"
            style={{ borderColor: 'hsl(43, 74%, 52%)' }}
          />
          
          <div className="mt-3 flex justify-center items-center space-x-2">
            <span className="text-2xl">❤️</span>
            <span className="text-sm font-medium" style={{ color: 'hsl(43, 74%, 52%)' }}>Expressed Interest</span>
          </div>
        </div>

        <div className="space-y-4">
          {/* Name */}
          <div className="bg-white bg-opacity-10 rounded-xl p-4 backdrop-blur-sm">
            <div className="flex items-center space-x-3">
              <div className="rounded-full p-2" style={{ backgroundColor: 'hsl(43, 74%, 52%)' }}>
                <UserIcon className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-white text-opacity-70 mb-1">Name</p>
                <p className="text-white font-semibold">
                  {interestData.senderProfile?.firstName} {interestData.senderProfile?.lastName}
                </p>
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="bg-white bg-opacity-10 rounded-xl p-4 backdrop-blur-sm">
            <div className="flex items-center space-x-3">
              <div className="rounded-full p-2" style={{ backgroundColor: 'hsl(43, 74%, 52%)' }}>
                <LocationIcon className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-white text-opacity-70 mb-1">Location</p>
                <p className="text-white font-semibold">
                  {interestData.senderProfile?.city}, {interestData.senderProfile?.state}
                </p>
              </div>
            </div>
          </div>

          {/* Denomination */}
          <div className="bg-white bg-opacity-10 rounded-xl p-4 backdrop-blur-sm">
            <div className="flex items-center space-x-3">
              <div className="rounded-full p-2" style={{ backgroundColor: 'hsl(43, 74%, 52%)' }}>
                <ChurchIcon className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-white text-opacity-70 mb-1">Denomination</p>
                <p className="text-white font-semibold">
                  {interestData.senderProfile?.denomination || 'Not specified'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 p-3 bg-white bg-opacity-10 rounded-lg">
          <p className="text-xs text-white text-opacity-80 text-center">
            This person has expressed interest in you. Accept their interest to unlock more features.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function MyInterestsPage() {
  const { userId, db, isAuthReady } = useContext(AuthContext);
  const [receivedInterests, setReceivedInterests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [showBasicInfo, setShowBasicInfo] = useState(null);
  const [processingInterest, setProcessingInterest] = useState(null);
  

  useEffect(() => {
    let unsubscribeInterests;
    let senderUnsubscribers = [];
    
    if (isAuthReady && userId) {
      setLoading(true);
      const interestsRef = collection(db, 'interests');
      
      // Updated query to only show pending and accepted interests
      // This excludes rejected and removed interests
      const q = query(
        interestsRef, 
        where('receiverId', '==', userId),
        where('status', 'in', ['pending', 'accepted'])
      );

      unsubscribeInterests = onSnapshot(q, async (snapshot) => {
        const interestsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Additional filter to ensure uniqueness by senderId
        // This handles any edge cases where duplicates might exist
        const uniqueInterests = interestsData.reduce((acc, interest) => {
          const existing = acc.find(i => i.senderId === interest.senderId);
          if (!existing) {
            acc.push(interest);
          } else {
            // Keep the most recent one if duplicates exist
            const currentTime = interest.sentAt?.toMillis() || 0;
            const existingTime = existing.sentAt?.toMillis() || 0;
            if (currentTime > existingTime) {
              const index = acc.indexOf(existing);
              acc[index] = interest;
            }
          }
          return acc;
        }, []);

        // First set interests without profiles to show data quickly
        setReceivedInterests(uniqueInterests.map(interest => ({ ...interest, senderProfile: null })));
        setLoading(false);

        // Clean up previous sender listeners
        senderUnsubscribers.forEach(unsub => unsub());
        senderUnsubscribers = [];

        // Then set up real-time listeners for each sender profile
        uniqueInterests.forEach((interest) => {
          if (interest.senderId) {
            const senderDocRef = doc(db, 'users', interest.senderId);
            
            // Listen to real-time updates for this sender's profile
            const unsubscribeSender = onSnapshot(senderDocRef, (senderDocSnap) => {
              setReceivedInterests(prevInterests => {
                const updatedInterests = [...prevInterests];
                const interestIndex = updatedInterests.findIndex(i => i.id === interest.id);
                
                if (interestIndex !== -1) {
                  updatedInterests[interestIndex] = {
                    ...updatedInterests[interestIndex],
                    senderProfile: senderDocSnap.exists() ? senderDocSnap.data() : null
                  };
                }
                
                return updatedInterests;
              });
            }, (error) => {
              console.error(`Error fetching sender profile for ${interest.senderId}:`, error);
            });
            
            senderUnsubscribers.push(unsubscribeSender);
          }
        });
      }, (error) => {
        console.error("Error fetching received interests:", error);
        setMessage(`Error loading interests: ${error.message}`);
        setLoading(false);
      });
    }

    return () => {
      if (unsubscribeInterests) unsubscribeInterests();
      // Cleanup all sender profile listeners
      senderUnsubscribers.forEach(unsub => unsub());
    };
  }, [userId, db, isAuthReady]);

  const handleInterestAction = async (interestId, newStatus) => {
    setMessage('');
    setProcessingInterest(interestId);
    
    try {
      const interestDocRef = doc(db, 'interests', interestId);
      await updateDoc(interestDocRef, { 
        status: newStatus,
        updatedAt: new Date()
      });
      setMessage(`Interest ${newStatus} successfully! ${newStatus === 'accepted' ? '❤️' : ''}`);
    } catch (error) {
      setMessage(`Failed to ${newStatus} interest: ${error.message}`);
      console.error(`Error updating interest status to ${newStatus}:`, error);
    } finally {
      setProcessingInterest(null);
    }
  };

  const handleRemoveInterest = async (interestId) => {
    setMessage('');
    setProcessingInterest(interestId);
    
    try {
      // Update the status to 'removed' - this will exclude it from the query
      const interestDocRef = doc(db, 'interests', interestId);
      await updateDoc(interestDocRef, { 
        status: 'removed',
        removedBy: 'receiver',
        removedAt: new Date()
      });
      setMessage('Interest removed successfully!');
      
      // Remove from local state immediately for better UX
      setReceivedInterests(prev => prev.filter(interest => interest.id !== interestId));
    } catch (error) {
      setMessage(`Failed to remove interest: ${error.message}`);
      console.error('Error removing interest:', error);
    } finally {
      setProcessingInterest(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted': return 'text-white border-white bg-opacity-20';
      case 'rejected': return 'text-white border-white bg-opacity-20';
      default: return 'text-white border-white bg-opacity-20';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'accepted': return '❤️';
      case 'rejected': return '💔';
      default: return '⏳';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white pt-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: 'hsl(43, 74%, 52%)' }}></div>
          <p className="text-xl font-semibold" style={{ color: 'hsl(221, 45%, 18%)' }}>Loading your interests...</p>
          <p className="text-sm mt-2" style={{ color: 'hsl(221, 45%, 18%)', opacity: 0.7 }}>Finding people who like you...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pt-24 pb-12">
      <div className="container mx-auto px-6">
        <div className="text-center mb-8">
          <h2 className="text-4xl font-extrabold mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>
            My Interests ❤️
          </h2>
          <p className="text-lg" style={{ color: 'hsl(221, 45%, 18%)', opacity: 0.8 }}>People who have expressed interest in you</p>
          <div className="mt-2 text-sm" style={{ color: 'hsl(221, 45%, 18%)', opacity: 0.7 }}>
            Accept their interest with ❤️ to view basic information
          </div>
        </div>

        {message && <MessageBox message={message} onClose={() => setMessage('')} />}

        {receivedInterests.length === 0 ? (
          <div className="bg-white p-8 rounded-2xl shadow-xl text-center border-2" style={{ borderColor: 'hsl(221, 45%, 18%)' }}>
            <div className="text-6xl mb-4">👀</div>
            <p className="text-xl font-semibold mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>
              No interests received yet.
            </p>
            <p style={{ color: 'hsl(221, 45%, 18%)', opacity: 0.7 }}>When someone expresses interest in you with ❤️, it will appear here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {receivedInterests.map((interest) => (
              <div 
                key={interest.id} 
                className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border-2"
                style={{ borderColor: 'hsl(43, 74%, 52%)' }}
              >
                {interest.senderProfile ? (
                  <>
                    <div className="text-center mb-4">
                      <div className="relative inline-block">
                        <img
                          src={interest.senderProfile.profilePhotoUrl || "https://placehold.co/100x100/E0E7FF/4F46E5?text=No+Photo"}
                          alt={interest.senderProfile.firstName || "Sender"}
                          className="w-24 h-24 object-cover rounded-full mb-4 border-4 mx-auto shadow-lg"
                          style={{ borderColor: 'hsl(43, 74%, 52%)' }}
                        />
                        <div 
                          className="absolute -bottom-2 -right-2 rounded-full p-1 shadow-md"
                          style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }}
                        >
                          <span className="text-white text-lg">❤️</span>
                        </div>
                      </div>
                      <h3 className="text-xl font-semibold mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>
                        {interest.senderProfile.firstName} {interest.senderProfile.lastName}
                      </h3>
                      <p className="text-sm mb-2" style={{ color: 'hsl(221, 45%, 18%)', opacity: 0.7 }}>
                        📍 {interest.senderProfile.city}, {interest.senderProfile.state}
                      </p>
                      <p className="text-xs mb-4" style={{ color: 'hsl(221, 45%, 18%)', opacity: 0.6 }}>
                        ❤️ Expressed interest: {interest.sentAt?.toDate().toLocaleDateString()}
                      </p>
                    </div>

                    <div className="text-center mb-4">
                      <div 
                        className={`inline-flex items-center space-x-2 px-3 py-2 rounded-full border ${getStatusColor(interest.status)}`}
                        style={{ 
                          backgroundColor: interest.status === 'accepted' 
                            ? 'hsl(43, 74%, 52%)' 
                            : interest.status === 'rejected' 
                            ? 'hsl(221, 45%, 18%)' 
                            : 'hsl(43, 74%, 52%)' 
                        }}
                      >
                        <span className="text-lg">{getStatusIcon(interest.status)}</span>
                        <span className="text-sm font-bold">
                          {interest.status.charAt(0).toUpperCase() + interest.status.slice(1)}
                        </span>
                      </div>
                    </div>

                    {interest.status === 'pending' && (
                      <div className="space-y-3">
                        <button
                          onClick={() => handleInterestAction(interest.id, 'accepted')}
                          disabled={processingInterest === interest.id}
                          className="w-full px-4 py-3 rounded-xl flex items-center justify-center space-x-2 text-white hover:shadow-lg border-2 transition-all duration-300 font-semibold disabled:opacity-50"
                          style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)', borderColor: 'hsl(43, 74%, 52%)' }}
                          onMouseEnter={(e) => !processingInterest && (e.currentTarget.style.transform = 'translateY(-2px)')}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                        >
                          <HeartIcon className="w-5 h-5" filled />
                          <span>Accept Interest ❤️</span>
                        </button>
                        <button
                          onClick={() => handleInterestAction(interest.id, 'rejected')}
                          disabled={processingInterest === interest.id}
                          className="w-full px-4 py-2 bg-white rounded-xl hover:opacity-80 transition duration-300 disabled:opacity-30 font-semibold border-2"
                          style={{ color: 'hsl(221, 45%, 18%)', borderColor: 'hsl(221, 45%, 18%)' }}
                        >
                          Not Interested 💔
                        </button>
                      </div>
                    )}

                    {interest.status === 'accepted' && (
                      <div className="space-y-3">
                        <div className="rounded-xl p-3 text-center border-2" style={{ backgroundColor: 'hsl(43, 74%, 52%)', borderColor: 'hsl(43, 74%, 52%)' }}>
                          <span className="text-white font-semibold">
                            ❤️ Mutual Interest! ❤️
                          </span>
                        </div>
                        <button
                          onClick={() => setShowBasicInfo(interest)}
                          className="w-full px-4 py-3 text-white rounded-xl hover:shadow-lg transition-all duration-300 flex items-center justify-center space-x-2 font-semibold"
                          style={{ 
                            background: 'linear-gradient(135deg, hsl(221, 45%, 18%) 0%, hsl(221, 35%, 25%) 100%)'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                        >
                          <span>👤</span>
                          <span>View Basic Info</span>
                        </button>
                        <button
                          onClick={() => handleRemoveInterest(interest.id)}
                          disabled={processingInterest === interest.id}
                          className="w-full px-4 py-2 bg-white rounded-xl hover:opacity-80 transition duration-300 disabled:opacity-30 font-semibold border-2"
                          style={{ color: 'hsl(221, 45%, 18%)', borderColor: 'hsl(221, 45%, 18%)' }}
                        >
                          🗑️ Remove Interest
                        </button>
                      </div>
                    )}

                    {interest.status === 'rejected' && (
                      <div className="space-y-3">
                        <div className="rounded-xl p-3 text-center border-2" style={{ backgroundColor: 'hsl(221, 45%, 18%)', borderColor: 'hsl(221, 45%, 18%)' }}>
                          <span className="text-white font-semibold">
                            💔 Interest Declined
                          </span>
                        </div>
                        <button
                          onClick={() => handleRemoveInterest(interest.id)}
                          disabled={processingInterest === interest.id}
                          className="w-full px-4 py-2 bg-white rounded-xl hover:opacity-80 transition duration-300 disabled:opacity-30 font-semibold border-2"
                          style={{ color: 'hsl(221, 45%, 18%)', borderColor: 'hsl(221, 45%, 18%)' }}
                        >
                          🗑️ Remove Interest
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-center" style={{ color: 'hsl(221, 45%, 18%)' }}>Unable to load sender profile</p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Basic Info Modal */}
        <BasicInfoModal
          isOpen={!!showBasicInfo}
          onClose={() => setShowBasicInfo(null)}
          interestData={showBasicInfo}
        />
      </div>
    </div>
  );
}